/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { DataTable } from '@/components/CustomTable'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn, formatCFA, formatDateFR } from '@/lib/utils'
import {
  useCustomerQuery,
  useCustomerBalanceQuery,
  useCustomerAccountQuery,
} from '@/hooks/useCustomers'
import type { ColumnDef } from '@tanstack/react-table'
import BackBtn from '@/components/BackButton'
import { useServerTableControls } from '@/hooks/useServerTableControls'
import Loading from '@/components/Loading'
import { Button } from '@/components/ui/button'
import { ChevronDown, ChevronUp, Download, Eye, Wallet } from 'lucide-react'
import AppDialog, { ModalActions } from '@/components/CustomModal'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useCreatePaymentMutation } from '@/hooks/usePayments'
import { Switch } from '@/components/ui/switch'
import { toast } from 'sonner'
import { useCustomerExport } from '@/hooks/useCustomerExport'
import { useState } from 'react'

export default function CustomerAccountPage() {
  const { id } = useParams<{ id: string }>()
  const { data: customer, isFetching: isCustomerFetching } = useCustomerQuery(
    id,
    !!id,
  )
  const { data: balance } = useCustomerBalanceQuery(id, !!id)
  const navigate = useNavigate()
  const location = useLocation()
  const back = new URLSearchParams(location.search).get('back')
  const { exportAccountCsv } = useCustomerExport()
  const hasPhone = !!customer?.phone?.trim()
  const [remindEnabled, setRemindEnabled] = ((
    initial: boolean,
  ): [boolean, (v: boolean) => void] => {
    const [v, setV] = useState(initial)
    return [v, setV]
  })(true)

const [balanceOpen, setBalanceOpen] = useState(false)
  const [relanceOpen, setRelanceOpen] = useState(false)
  const [globalPayOpen, setGlobalPayOpen] = useState(false)
  const [globalAmount, setGlobalAmount] = useState('')
  const createPayment = useCreatePaymentMutation()

  const { pageIndex, pageSize, tableProps } = useServerTableControls({
    initialPageSize: 100,
  })

  const {
    data: page,
    status,
    isFetching,
  } = useCustomerAccountQuery(
    id,
    { page: pageIndex + 1, pageSize, dir: 'desc' },
    !!id,
  )

  const lines = page?.lines ?? []
  const total = page?.pagination.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / Math.max(1, pageSize)))

  // =========================
  // ✅ DONNÉES API À AFFICHER
  // =========================
  const openDebt = balance?.openDebt ?? 0 // dette sur factures ouvertes
  const receivable = balance?.receivable ?? 0 // net à recevoir (après avoir)
  const credit = balance?.credit ?? 0 // avoir disponible

  const opening = page?.opening ?? 0 // solde avant la période affichée
  const closing = page?.closing ?? 0 // solde après la dernière ligne

  // tone de la carte principale basé sur net à recevoir / avoir
  const cardTone =
    receivable > 0
      ? 'border-red-300 bg-red-50 text-red-900 dark:border-red-900/40 dark:bg-red-950/30 dark:text-red-300'
      : credit > 0
        ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300'
        : 'border-muted bg-muted/30 text-foreground/80'

  const title =
    receivable > 0 ? 'À recevoir' : credit > 0 ? 'Avoir client' : 'Solde à zéro'
  const subtitle =
    receivable > 0
      ? 'Le client vous doit'
      : credit > 0
        ? 'Vous devez au client (avoir disponible)'
        : 'Aucun dû'

  // ----- Colonnes
  type Line = (typeof lines)[number]
  const columns: ColumnDef<Line>[] = [
    {
      accessorKey: 'date',
      header: 'Date',
      cell: ({ row }) =>
        formatDateFR(row.original.date, {
          showTime: 'auto',
          withSeconds: true,
        }),
    },
    {
      accessorKey: 'kind',
      header: 'Type',
      cell: ({ row }) => {
        const k = row.original.kind
        const label =
          k === 'BILL' ? 'Facture' : k === 'PAYMENT' ? 'Paiement' : k
        return (
          <span className='inline-flex items-center rounded-full border px-2 py-0.5 text-xs'>
            {label}
          </span>
        )
      },
    },

    {
      accessorKey: 'credit',
      header: 'Payé',
      cell: ({ row }) => {
        const v = row.original.credit
        if (!v) return ''
        return (
          <span className='tabular-nums block text-emerald-600'>
            {formatCFA(v)}
          </span>
        )
      },
    },

    {
      accessorKey: 'debit',
      header: 'Facturé',
      cell: ({ row }) => {
        const v = row.original.debit
        if (!v) return ''
        return (
          <span className='tabular-nums block text-red-600'>
            {formatCFA(v)}
          </span>
        )
      },
    },

    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => row.original.description ?? '—',
    },
    {
      accessorKey: 'balance',
      header: 'Solde',
      cell: ({ row }) => {
        const v = row.original.balance ?? 0
        const color =
          v > 0
            ? 'text-red-700'
            : v < 0
              ? 'text-emerald-700'
              : 'text-muted-foreground'
        return (
          <span className={cn('tabular-nums block font-semibold', color)}>
            {formatCFA(v)}
          </span>
        )
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => {
        const isBill = row.original.kind === 'BILL'
        const billId = row.original.id
        if (!isBill || !billId) return null
        return (
          <div className='flex justify-end gap-2'>
            <Button
              variant='ghost'
              size='sm'
              title='Voir facture'
              onClick={() => navigate(`/bills/${billId}`)}
            >
              <Eye className='h-4 w-4' />
            </Button>
            <Button
              variant='ghost'
              size='sm'
              title='Payer'
              onClick={() => navigate(`/bills/${billId}?action=pay#payments`)}
            >
              <Wallet className='h-4 w-4' />
            </Button>
          </div>
        )
      },
    },
  ]

  const canToggleRemind = !isCustomerFetching && (hasPhone || !!customer?.email)

  return (
    <div className='space-y-6'>
      <BackBtn to={back ? decodeURIComponent(back) : '/customers'} />

      <div className='flex items-start justify-between gap-4'>
        <div>
          <h3 className='text-2xl font-semibold'>{customer?.name}</h3>
          <p className='text-sm text-muted-foreground'>
            {customer?.phone ?? customer?.email ?? '—'}
          </p>

          {/* ✅ Relance (UI-only pour l’instant) */}
          <div className='mt-4'>
            <button
              className='flex items-center gap-2 text-sm cursor-pointer select-none'
              onClick={() => setRelanceOpen((v) => !v)}
            >
              <span>Relance</span>
              {relanceOpen ? (
                <ChevronUp className='h-3 w-3 opacity-60' />
              ) : (
                <ChevronDown className='h-3 w-3 opacity-60' />
              )}
              {!relanceOpen && (
                <span
                  className={cn(
                    'text-xs rounded-full border px-2 py-0.5',
                    remindEnabled
                      ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300'
                      : 'text-muted-foreground',
                  )}
                >
                  {remindEnabled ? 'Actif' : 'Inactif'}
                </span>
              )}
            </button>

            {relanceOpen && (
              <div className='mt-2 space-y-2'>
                <div className='flex items-center gap-3'>
                  <Switch
                    checked={remindEnabled}
                    disabled={!canToggleRemind}
                    onCheckedChange={(v) => {
                      if (v && !hasPhone && !customer?.email) {
                        toast.error('Ajoute un téléphone ou un email')
                        return
                      }
                      setRemindEnabled(v)
                    }}
                  />

                  <span
                    className={cn(
                      'text-xs rounded-full border px-2 py-0.5',
                      remindEnabled
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-900/40 dark:bg-emerald-950/30 dark:text-emerald-300'
                        : 'text-muted-foreground',
                    )}
                  >
                    {remindEnabled ? 'Actif' : 'Inactif'}
                  </span>
                </div>

                <div className='flex items-center gap-2'>
                  <Button
                    type='button'
                    variant='default'
                    disabled={!remindEnabled || !hasPhone}
                    title={!hasPhone ? 'Numéro requis pour WhatsApp' : undefined}
                  >
                    WhatsApp
                  </Button>

                  <span className='text-xs text-muted-foreground ml-2'>
                    Canal: <b>WhatsApp</b>
                  </span>
                </div>

                {!hasPhone && remindEnabled ? (
                  <div className='text-xs text-amber-700 dark:text-amber-300'>
                    Ajoute un numéro (+225...) pour activer les relances WhatsApp.
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>

        <div className='flex flex-col gap-8 md:flex-row'>
          <Button
            onClick={() => setGlobalPayOpen(true)}
            disabled={receivable <= 0}
          >
            <Wallet className='h-4 w-4 mr-2' />
            Encaisser
          </Button>

          <Button
            variant='outline'
            size='sm'
            onClick={async () => {
              try {
                await exportAccountCsv(
                  id!,
                  `compte-client-${customer?.name ?? id}.csv`,
                )
              } catch (e: any) {
                toast.error('Export impossible', {
                  description: e?.message ?? 'Erreur inconnue',
                })
              }
            }}
          >
            <Download className='h-4 w-4 mr-2' />
            Exporter CSV
          </Button>
        </div>
      </div>

      {/* ✅ Résumé compte */}
      <Card className={cn('max-w-xl border', cardTone)}>
        <CardHeader
          className='cursor-pointer select-none'
          onClick={() => setBalanceOpen((v) => !v)}
        >
          <div className='flex items-center justify-between'>
            <CardTitle className='text-lg'>{title}</CardTitle>
            {balanceOpen ? (
              <ChevronUp className='h-4 w-4 opacity-60' />
            ) : (
              <ChevronDown className='h-4 w-4 opacity-60' />
            )}
          </div>
          {!balanceOpen && (
            <div className='text-2xl font-bold tabular-nums mt-1'>
              {receivable > 0
                ? formatCFA(receivable)
                : credit > 0
                  ? formatCFA(credit)
                  : formatCFA(0)}
            </div>
          )}
        </CardHeader>

        {balanceOpen && (
          <CardContent className='space-y-3'>
            <div className='text-3xl font-bold tabular-nums'>
              {receivable > 0
                ? formatCFA(receivable)
                : credit > 0
                  ? formatCFA(credit)
                  : formatCFA(0)}
            </div>
            <div className='text-xs opacity-80'>{subtitle}</div>

            <div className='mt-2 grid grid-cols-2 gap-2 text-sm'>
              <div className='text-muted-foreground'>
                Dette ouverte (relançable)
              </div>
              <div className='text-right tabular-nums font-medium'>
                {formatCFA(openDebt)}
              </div>

              <div className='text-muted-foreground'>Avoir disponible</div>
              <div className='text-right tabular-nums font-medium'>
                {formatCFA(credit)}
              </div>

              <div className='text-muted-foreground'>Net à payer</div>
              <div className='text-right tabular-nums font-semibold'>
                {formatCFA(receivable)}
              </div>
            </div>

            <div className='pt-3 border-t text-xs grid grid-cols-2 gap-2 opacity-80'>
              <div>Solde initial (avant période)</div>
              <div className='text-right tabular-nums'>
                {formatCFA(opening)}
              </div>

              <div>Solde final (après lignes)</div>
              <div className='text-right tabular-nums'>
                {formatCFA(closing)}
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {status === 'pending' ? (
        <Loading />
      ) : (
        <>
          <DataTable<Line, unknown>
            columns={columns}
            data={lines}
            {...tableProps}
            total={total}
            pageCount={pageCount}
          />
          <div className='text-xs text-muted-foreground'>
            {isFetching ? 'Actualisation…' : null}
          </div>
        </>
      )}

      <AppDialog
        open={globalPayOpen}
        onOpenChange={setGlobalPayOpen}
        title='Encaisser un paiement'
        description={`Paiement pour ${
          customer?.name ?? ''
        }. Réduit la dette globale du client.`}
        asForm
        onSubmit={(e) => {
          e.preventDefault()
          const a = Number(globalAmount.replace(/\s/g, '').replace(',', '.'))
          if (!isFinite(a) || a <= 0) return
          createPayment.mutate(
            {
              customerId: id!,
              amount: a,
              method: 'CASH',
              applyToDebt: true,
            },
            {
              onSuccess: () => {
                setGlobalPayOpen(false)
                setGlobalAmount('')
              },
            },
          )
        }}
        footer={
          <ModalActions
            confirmLabel='Valider'
            confirmLoading={createPayment.isPending}
          />
        }
      >
        <div className='w-full px-4 py-2 space-y-3'>
          <Label htmlFor='global-amount'>Montant reçu</Label>
          <Input
            id='global-amount'
            inputMode='decimal'
            value={globalAmount}
            onChange={(e) => setGlobalAmount(e.target.value)}
            placeholder='Ex: 10000'
            autoFocus
          />
          <div className='text-xs text-muted-foreground'>
            Net à recevoir : <b>{formatCFA(receivable)}</b>
          </div>
        </div>
      </AppDialog>
    </div>
  )
}
